import type { ReserveTokensResult } from '../../types';
import type { OriginalEngineEntitlementGateway, OriginalEngineReservation } from '../types';

export interface ManuscriptEntitlementAdapterDeps {
  reserveTokens: (
    runId: string,
    projectId: string,
    chapterNum: number,
    operation: string,
    amount: number,
  ) => Promise<ReserveTokensResult>;
  consumeTokenReservation: (runId: string) => Promise<boolean>;
  releaseTokenReservation: (runId: string, failureReason?: string) => Promise<boolean>;
}

export class ManuscriptEntitlementAdapter implements OriginalEngineEntitlementGateway {
  constructor(private readonly deps: ManuscriptEntitlementAdapterDeps) {}

  async reserve(input: {
    projectId: string;
    chapterNumber: number;
    runId: string;
  }): Promise<OriginalEngineReservation> {
    const result = await this.deps.reserveTokens(
      input.runId,
      input.projectId,
      input.chapterNumber,
      'manuscript_generation',
      1,
    );
    if (!result.ok) {
      throw new ManuscriptEntitlementError(
        result.error?.reason || result.error?.kind || 'reservation_failed',
        result.error?.message || 'Reservation failed',
      );
    }
    return {
      reservationId: input.runId,
      runId: input.runId,
    };
  }

  async consume(reservationId: string): Promise<void> {
    const ok = await this.deps.consumeTokenReservation(reservationId);
    if (!ok) {
      throw new ManuscriptEntitlementError('consume_failed', 'Failed to consume reservation');
    }
  }

  async release(reservationId: string, reason: string): Promise<void> {
    await this.deps.releaseTokenReservation(reservationId, reason);
  }
}

export class ManuscriptEntitlementError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'ManuscriptEntitlementError';
    this.code = code;
  }
}

export class InMemoryOriginalEngineEntitlementGateway implements OriginalEngineEntitlementGateway {
  readonly events: Array<{ type: 'reserve' | 'consume' | 'release'; reservationId: string; reason?: string }> = [];
  private shouldFailCommit = false;
  private shouldFailTransport = false;

  setCommitFailure(enabled: boolean): void {
    this.shouldFailCommit = enabled;
  }

  async reserve(input: {
    projectId: string;
    chapterNumber: number;
    runId: string;
  }): Promise<OriginalEngineReservation> {
    this.events.push({ type: 'reserve', reservationId: input.runId });
    return { reservationId: input.runId, runId: input.runId };
  }

  async consume(reservationId: string): Promise<void> {
    if (this.shouldFailCommit) {
      throw new Error('Simulated consume failure');
    }
    this.events.push({ type: 'consume', reservationId });
  }

  async release(reservationId: string, reason: string): Promise<void> {
    this.events.push({ type: 'release', reservationId, reason });
  }
}

export function createTrackingEntitlementGateway(): {
  gateway: OriginalEngineEntitlementGateway;
  wasConsumed: () => boolean;
  wasReleased: () => boolean;
} {
  const events: Array<{ type: string; reservationId: string }> = [];
  const gateway: OriginalEngineEntitlementGateway = {
    async reserve(input) {
      events.push({ type: 'reserve', reservationId: input.runId });
      return { reservationId: input.runId, runId: input.runId };
    },
    async consume(reservationId) {
      events.push({ type: 'consume', reservationId });
    },
    async release(reservationId) {
      events.push({ type: 'release', reservationId });
    },
  };
  return {
    gateway,
    wasConsumed: () => events.some((e) => e.type === 'consume'),
    wasReleased: () => events.some((e) => e.type === 'release'),
  };
}
